import { ContactForm } from '../../components/forms/ContactForm';
import { DemoPopup } from '../../components/forms/DemoPopup';
import { Button } from '../../components/ui/button';
import {
  Megaphone,
  Mail,
  Share2,
  Target,
  RefreshCcw,
  BarChart3,
  FileText,
  CheckCircle2,
} from 'lucide-react';

const features = [
  {
    icon: Mail,
    title: 'Email Campaigns',
    description: 'Design, send, and track email campaigns with powerful automation.',
  },
  {
    icon: Share2,
    title: 'Social Media',
    description: 'Schedule posts, monitor engagement, and manage all channels in one place.',
  },
  {
    icon: Target,
    title: 'Lead Scoring',
    description: 'Score and prioritize leads based on engagement and behavior.',
  },
  {
    icon: RefreshCcw,
    title: 'Marketing Automation',
    description: 'Automate workflows, nurture sequences, and trigger-based campaigns.',
  },
  {
    icon: BarChart3,
    title: 'ROI Analytics',
    description: 'Track campaign performance and measure marketing ROI effectively.',
  },
  {
    icon: FileText,
    title: 'Landing Pages',
    description: 'Build high-converting landing pages without any coding required.',
  },
];

const benefits = [
  'Increase marketing ROI by 150%',
  'Automate repetitive marketing tasks',
  'Better lead qualification and nurturing',
  'Unified view of all marketing channels',
  'Data-driven campaign optimization',
  'Seamless CRM integration',
];

export const MarketingPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="marketing-page">
      {/* Hero Banner */}
      <section className="py-20 md:py-28 hero-glow" data-testid="marketing-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in-up">
              Marketing Management
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white animate-fade-in-up stagger-1">
              Marketing That Delivers Results
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mt-6 animate-fade-in-up stagger-2">
              Multi-channel campaigns, automation, and analytics to maximize your marketing ROI.
            </p>
            <div className="mt-8 animate-fade-in-up stagger-3">
              <DemoPopup
                title="Request Demo for Marketing Management"
                formType="Marketing Management Demo"
                defaultSolution="Marketing Management"
                trigger={
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium glow-primary glow-primary-hover btn-shine"
                    data-testid="marketing-hero-demo-btn"
                  >
                    Request Demo
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 md:py-32" data-testid="marketing-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
              Key Features
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Tools to supercharge your marketing efforts
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-${index + 1}`}
                data-testid={`marketing-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mb-6">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold font-heading text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 bg-secondary/30" data-testid="marketing-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Transform Your Marketing
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our Marketing Management solution helps you run more effective campaigns with less effort.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="animate-fade-in-up stagger-2">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.pexels.com/photos/5833267/pexels-photo-5833267.jpeg?w=800"
                  alt="Marketing Team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Form Section */}
      <section className="py-24 md:py-32" data-testid="marketing-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Request a Demo for Marketing Management
              </h2>
              <p className="text-muted-foreground text-lg mt-4">
                See how we can boost your marketing results.
              </p>
            </div>

            <div className="glass rounded-2xl p-8 animate-fade-in-up stagger-1" data-testid="marketing-demo-form-wrapper">
              <ContactForm
                formType="Marketing Management Demo"
                showInterestedIn={false}
                showTeamSize={true}
                interestedInDefault="Marketing Management"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketingPage;
